import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../../consts';

const COLUMN_ID = 'warehouse_pending_from_production_units' as const;

export type WarehousePendingFromProductionColumnId = typeof COLUMN_ID;

interface WarehousePendingFromProductionFields {
  warehouse_pending_from_production_units: number | null;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<WarehousePendingFromProductionFields>();

export const createWarehousePendingFromProductionColumn = <
  T extends WarehousePendingFromProductionFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.warehouse_pending_from_production_units),
    {
      id: COLUMN_ID,
      size: 200,
      header: function WarehousePendingFromProductionHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.warehouse-pending-from-production-units')}
              </DataTableCellTitle>
            </DataTableCellContent>
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function WarehousePendingFromProductionCell({
            getValue,
          }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
